import { forwardRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  FormHelperText,
  TextField,
  Typography,
} from '@mui/material';
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { setGlobalMessage } from 'hooks/useGlobalMessage';
import { useSaveSignalSetting } from 'applications/signal/hooks/gqlHooks';
import { transformBrandSignalResponseToSampleData } from 'applications/signal/utils/dataUtils';
import { SignalNoticeTypes } from 'applications/signal/constants';
import BrandSignalSampleList, { BrandSignalSample } from '../BrandSignalSampleList';
import FullScreenLoadCircle from '../FullScreenLoadCircle';
import { DEFAULT_NOTICE_TYPE } from '../NotificationSettingEditForm';
import { NotificationSettingPostFormData, NotificationSettingPostFormSchema } from './schema';

type NotificationSettingPostFormProps = {
  condition: {
    id?: string;
    name?: string;
    signalType: number;
    noticeType?: number;
    active?: boolean;
    categories: string[];
    areas: string[];
    channels: string[];
    genderAges: string[];
    shopChains: string[];
    targetDays: number;
    targetBrand: string;
    comperetiveBrands: string[];
    minThreshold?: number;
    maxThreshold?: number;
  },
  sampleResponse: any,
  onClickRecalculate?: () => void,
}

const NotificationSettingPostForm = forwardRef<
  HTMLDivElement, NotificationSettingPostFormProps
>(({ condition, sampleResponse, onClickRecalculate }, ref) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const { saveSignalSetting } = useSaveSignalSetting();
  const {
    control,
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<NotificationSettingPostFormData>({
    resolver: zodResolver(NotificationSettingPostFormSchema),
    defaultValues: {
      name: condition.name || "",
      minThreshold: condition.minThreshold || 0,
      maxThreshold: condition.maxThreshold || 0,
      noticeType: condition.noticeType || DEFAULT_NOTICE_TYPE,
    },
  });

  // ブランドごとの最大・最小値を選択して表示するためのデータを整形
  const brandedSampleData: BrandSignalSample[] = transformBrandSignalResponseToSampleData(
    condition.signalType, sampleResponse
  );

  const onSubmit = async (data: NotificationSettingPostFormData) => {
    setIsLoading(true);
    const { data: result, error } = await saveSignalSetting({data: {
      id: condition.id || null,
      active: condition.active || true,
      name: data.name,
      signal_type: condition.signalType,
      notice_type: condition.noticeType || 0,
      categories: condition.categories,
      areas: condition.areas,
      channels: condition.channels,
      gender_ages: condition.genderAges,
      shop_chains: condition.shopChains,
      target_days: condition.targetDays,
      target_brand_name: condition.targetBrand,
      min_threshold: data.minThreshold,
      max_threshold: data.maxThreshold,
    }});
    setIsLoading(false);
    if (error) {
      setGlobalMessage({
        message: "Signal設定の保存に失敗しました。",
        severity: "error",
      });
      return;
    } else {
      setGlobalMessage({
        message: "Signal設定を保存しました。",
        severity: "success",
      });
      navigate("/signal/");
    }
  };

  return (
    <>
      {isLoading && <FullScreenLoadCircle />}
      <BrandSignalSampleList
        signalType={condition.signalType}
        samples={brandedSampleData}
      />
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <Box ref={ref} sx={{ maxWidth: 600, mx: "auto" }}>
          <Typography variant="h5" gutterBottom sx={{ mt: 5 }}>
            Signal設定登録
          </Typography>
          <FormControl
            fullWidth
            margin="normal"
          >
            <TextField
              {...register("name")}
              variant="standard"
              fullWidth
              label="設定名"
              error={!!errors.name}
              helperText={errors.name?.message}
            />
          </FormControl>
          <FormControl
            fullWidth
            margin="normal"
          >
            <TextField
              {...register("maxThreshold")}
              variant="standard"
              inputProps={{
                inputMode: "numeric",
                pattern: "[0-9]*",
              }}
              fullWidth
              label="最大閾値"
              error={!!errors.maxThreshold}
              helperText={errors.maxThreshold?.message}
            />
          </FormControl>
          <FormControl
            fullWidth
            margin="normal"
          >
            <TextField
              {...register("minThreshold")}
              variant="standard"
              fullWidth
              label="最小閾値"
              error={!!errors.minThreshold}
              helperText={errors.minThreshold?.message}
            />
          </FormControl>
          <FormControl
            fullWidth
            margin="normal"
            error={!!errors.noticeType}
          >
            <Controller
              name="noticeType"
              control={control}
              render={({ field }) => (
                <Autocomplete
                  options={SignalNoticeTypes}
                  value={SignalNoticeTypes.find(option => option.id === field.value) || null}
                  onChange={(_, newValue) => {
                    field.onChange(newValue?.id || DEFAULT_NOTICE_TYPE);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label={"通知タイプ"}
                      placeholder={"選択または検索"}
                    />
                  )}
                />
              )}
            />
            <FormHelperText>
              {errors.noticeType?.message}
            </FormHelperText>
          </FormControl>
          <Box display="flex" gap={2}>
            <Button
              variant="text"
              fullWidth
              sx={{ mt: 3, mb: 2 }}
              onClick={onClickRecalculate}
            >
              条件変更して再計算
            </Button>
            <Button
              type="submit"
              variant="contained"
              fullWidth
              sx={{ mt: 3, mb: 2 }}
              disabled={isSubmitting}
            >
              保存
            </Button>
          </Box>
        </Box>
      </form>
    </>
  )
});

export default NotificationSettingPostForm;
NotificationSettingPostForm.displayName = 'NotificationSettingPostForm';
